import React, {
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import useCallbackRef from 'hooks/useCallbackRef';
import utils from '../../utils';

import './InfiniteScroll.styles.scss';

type InfiniteScrollProps = {
  treshold?: number;
  debounceTime?: number;
  className?: string;
  children?: React.ReactNode;
  localScroll?: boolean;
  scrollRef?: (element: HTMLDivElement) => void;
  onScroll: () => void;
} & Omit<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
  'ref'
>;

export type InfiniteScrollRef = {
  container: HTMLDivElement;
};

const InfiniteScroll: React.ForwardRefRenderFunction<
  InfiniteScrollRef,
  InfiniteScrollProps
> = (props, ref) => {
  const {
    children,
    treshold = 90,
    debounceTime = 150,
    onScroll,
    localScroll = false,
    className,
    ...rest
  } = props;

  const [container, containerRef] = useCallbackRef<HTMLDivElement>();

  const handleScroll = useCallback(
    (element: HTMLElement) => {
      if (!element) return;

      if (
        ((element.clientHeight + Math.abs(element.scrollTop)) /
          element.scrollHeight) *
          100 >=
        treshold
      ) {
        onScroll?.();
      }
    },
    [onScroll, treshold],
  );

  useEffect(() => {
    if (!container) return;

    const handleWindowScroll = utils.debounce(
      () => handleScroll(localScroll ? container : document.documentElement),
      debounceTime,
    );

    (localScroll ? container : window).addEventListener(
      'scroll',
      handleWindowScroll,
      true,
    );

    return () => {
      (localScroll ? container : window).removeEventListener(
        'scroll',
        handleWindowScroll,
        true,
      );
    };
  }, [handleScroll, localScroll, debounceTime, container]);

  useImperativeHandle(
    ref,
    () => ({
      container,
    }),
    [container],
  );

  return (
    <div ref={containerRef} className={className} {...rest}>
      {children}
    </div>
  );
};

export default forwardRef(InfiniteScroll);
